import React from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  canManuallyResizePlaylistPicker,
  clampPlaylistPickerSize,
  nextPlaylistPickerSize,
  playlistPickerViewport,
  readStoredPlaylistPickerSize,
  writeStoredPlaylistPickerSize,
} from '../../utils/playlistPickerLayout';

const SIZE_CLASS = {
  sm: 'rhythm-modal--sm',
  md: '',
};

/**
 * Shared dialog shell for creation, invite, and confirmations.
 *
 * `size="md"` (default) scales from ~94vw on phones to 480–850px on large
 * displays. `size="sm"` keeps short confirmations compact. `layout="fill"`
 * pins the footer and lets the body consume leftover viewport height.
 *
 * `resizable` is opt-in for the playlist picker only: desktop users can drag
 * the bottom-right corner; phones and tablets keep viewport-responsive sizing.
 */
export default function Modal({
  open,
  title,
  onClose,
  children,
  footer = null,
  size = 'md',
  layout = 'stack',
  resizable = false,
}) {
  const dialogRef = React.useRef(null);
  const draggingRef = React.useRef(false);
  const dragOriginRef = React.useRef(null);
  const [desktopResize, setDesktopResize] = React.useState(false);
  const [userSize, setUserSize] = React.useState(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  React.useEffect(() => {
    if (!open || !resizable) {
      setDesktopResize(false);
      setUserSize(null);
      return undefined;
    }

    const sync = () => {
      const viewport = playlistPickerViewport();
      const allowed = canManuallyResizePlaylistPicker({ viewportWidth: viewport.width });
      setDesktopResize(allowed);
      setUserSize(allowed ? readStoredPlaylistPickerSize(viewport) : null);
    };
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, [open, resizable]);

  const stopResize = React.useCallback(() => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    dragOriginRef.current = null;
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    setUserSize((current) => {
      if (!current) return current;
      return writeStoredPlaylistPickerSize(current);
    });
  }, []);

  React.useEffect(() => {
    if (!desktopResize) return undefined;

    const onPointerMove = (event) => {
      if (!draggingRef.current || !dragOriginRef.current) return;
      const next = nextPlaylistPickerSize(dragOriginRef.current, {
        x: event.clientX,
        y: event.clientY,
      });
      setUserSize(next);
    };
    const onPointerUp = () => stopResize();

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, [desktopResize, stopResize]);

  const handleResizePointerDown = (event) => {
    if (!desktopResize) return;
    if (event.button != null && event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();

    const rect = dialogRef.current?.getBoundingClientRect();
    const current = clampPlaylistPickerSize({
      width: userSize?.width || rect?.width,
      height: userSize?.height || rect?.height,
    });
    draggingRef.current = true;
    dragOriginRef.current = {
      x: event.clientX,
      y: event.clientY,
      width: current.width,
      height: current.height,
    };
    setUserSize(current);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'se-resize';
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  if (!open) return null;

  const sizeClass = SIZE_CLASS[size] || '';
  const fillClass = layout === 'fill' ? 'rhythm-modal--fill' : '';
  const resizeClass = resizable && desktopResize ? 'rhythm-modal--resizable' : '';
  const userSizedClass = userSize ? 'rhythm-modal--user-sized' : '';
  const userStyle = userSize
    ? {
      width: `${userSize.width}px`,
      height: `${userSize.height}px`,
      maxWidth: `${userSize.width}px`,
      maxHeight: `${userSize.height}px`,
    }
    : undefined;

  return createPortal(
    <div className="rhythm-modal-overlay fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={userStyle}
        className={`rhythm-modal relative rounded-2xl border border-white/10 bg-gray-900 shadow-2xl ${sizeClass} ${fillClass} ${resizeClass} ${userSizedClass}`.trim()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <h3 className="rhythm-modal__title font-semibold text-white">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="shrink-0 rounded-lg p-1 text-gray-400 hover:bg-white/10 hover:text-white"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="rhythm-modal__body">{children}</div>

        {footer && <div className="rhythm-modal__footer">{footer}</div>}

        {resizable && desktopResize && (
          <button
            type="button"
            aria-label="Resize playlist picker"
            className="rhythm-modal__resize-handle"
            onPointerDown={handleResizePointerDown}
          />
        )}
      </div>
    </div>,
    document.body,
  );
}
